import React, { useState, useMemo } from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const money = (v) => '$' + num(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const int = (v) => num(v).toLocaleString();
const pct = (v) => num(v).toFixed(1) + '%';
const dateFmt = (v) => { if (!v) return '—'; const d = new Date(v); return isNaN(d) ? String(v) : d.toLocaleDateString(); };
const statusLabel = (s) => (s === 'IN_ERROR' ? 'In Error' : String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()));

const FMT = { money, int, pct, date: dateFmt, text: (v) => (v === null || v === undefined || v === '' ? '—' : String(v)), status: statusLabel };
const NUMERIC = new Set(['money', 'int', 'pct']);

// A sortable, horizontally-scrollable table.
function DataTable({ title, subtitle, columns, rows }) {
  const [sortKey, setSortKey] = useState(null);
  const [dir, setDir] = useState('desc');

  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const col = columns.find((c) => c.key === sortKey);
    const numeric = col && NUMERIC.has(col.type);
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (numeric) return (num(av) - num(bv)) * (dir === 'asc' ? 1 : -1);
      return String(av ?? '').localeCompare(String(bv ?? '')) * (dir === 'asc' ? 1 : -1);
    });
    return copy;
  }, [rows, sortKey, dir, columns]);

  const onSort = (key) => {
    if (sortKey === key) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setDir('desc'); }
  };

  if (!rows || rows.length === 0) {
    return (
      <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        <p className="mt-3 rounded-lg border border-dashed border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-500">No rows for this selection.</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-baseline justify-between">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
          {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
        </div>
        <span className="text-xs text-gray-400">{rows.length} rows</span>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
              {columns.map((c) => (
                <th key={c.key} onClick={() => onSort(c.key)}
                  className={`cursor-pointer select-none whitespace-nowrap px-3 py-2 font-medium hover:text-gray-800 ${NUMERIC.has(c.type) ? 'text-right' : ''}`}
                  title="Click to sort">
                  <span className="inline-flex items-center gap-1">
                    {c.label}
                    {sortKey === c.key && (dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sorted.map((r, i) => (
              <tr key={i} className="hover:bg-gray-50">
                {columns.map((c) => (
                  <td key={c.key} className={`whitespace-nowrap px-3 py-2 text-gray-700 ${NUMERIC.has(c.type) ? 'text-right tabular-nums' : ''}`}>
                    {(FMT[c.type] || FMT.text)(r[c.key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Build table row-sets per report type from the (unwrapped) data.
function tablesFor(type, data) {
  const T = [];
  if (type === 'chargebacks') {
    const claims = (data.chargebacks || []).map((c) => ({
      claim: c.claimNumber || c.claimId || (c.id ? String(c.id).slice(0, 8) : '—'),
      customer: c.customer?.name || c.customerName || '—',
      date: c.claimDate || c.createdAt, status: statusLabel(c.status), amount: num(c.totalAmount)
    }));
    T.push({ title: 'Claims', subtitle: 'Individual chargeback claims (up to 100)', rows: claims, columns: [
      { key: 'claim', label: 'Claim', type: 'text' }, { key: 'customer', label: 'Customer', type: 'text' },
      { key: 'date', label: 'Date', type: 'date' }, { key: 'status', label: 'Status', type: 'text' },
      { key: 'amount', label: 'Amount', type: 'money' }] });
    T.push({ title: 'By Status', rows: (data.statusBreakdown || []).map((s) => ({
      status: statusLabel(s.status), count: num(s.count), amount: num(s.totalAmount), avg: num(s.avgAmount), pctv: num(s.percentage) })),
      columns: [{ key: 'status', label: 'Status', type: 'text' }, { key: 'count', label: 'Claims', type: 'int' },
        { key: 'amount', label: 'Amount', type: 'money' }, { key: 'avg', label: 'Avg', type: 'money' }, { key: 'pctv', label: '% of Claims', type: 'pct' }] });
  } else if (type === 'customers') {
    T.push({ title: 'Customers', rows: (data.customers || []).map((c) => ({
      name: c.customer?.name || '—', type: c.customer?.entityType || c.customer?.type || '—', state: c.customer?.state || '—',
      count: num(c.metrics?.totalChargebacks), amount: num(c.metrics?.totalAmount), avg: num(c.metrics?.avgAmount) })),
      columns: [{ key: 'name', label: 'Customer', type: 'text' }, { key: 'type', label: 'Type', type: 'text' },
        { key: 'state', label: 'State', type: 'text' }, { key: 'count', label: 'Chargebacks', type: 'int' },
        { key: 'amount', label: 'Amount', type: 'money' }, { key: 'avg', label: 'Avg', type: 'money' }] });
  } else if (type === 'products') {
    T.push({ title: 'Products', rows: (data.products || []).map((p) => ({
      name: p.product?.name || '—', ndc: p.product?.ndc11 || '—', cls: p.product?.therapeuticClass || '—',
      count: num(p.performance?.totalChargebackCount), amount: num(p.performance?.totalChargebackAmount), customers: num(p.performance?.uniqueCustomers) })),
      columns: [{ key: 'name', label: 'Product', type: 'text' }, { key: 'ndc', label: 'NDC', type: 'text' },
        { key: 'cls', label: 'Therapeutic Class', type: 'text' }, { key: 'count', label: 'Chargebacks', type: 'int' },
        { key: 'customers', label: 'Customers', type: 'int' }, { key: 'amount', label: 'Amount', type: 'money' }] });
  } else if (type === 'contracts') {
    const rows = Array.isArray(data) ? data : (data.contracts || []);
    T.push({ title: 'Contracts', rows: rows.map((c) => ({
      number: c.contract?.number || '—', type: c.contract?.type || '—', status: statusLabel(c.contract?.status),
      customers: num(c.metrics?.customerCount), prices: num(c.metrics?.priceCount), aliases: num(c.metrics?.aliasCount) })),
      columns: [{ key: 'number', label: 'Contract', type: 'text' }, { key: 'type', label: 'Type', type: 'text' },
        { key: 'status', label: 'Status', type: 'text' }, { key: 'customers', label: 'Customers', type: 'int' },
        { key: 'prices', label: 'Price Rows', type: 'int' }, { key: 'aliases', label: 'Aliases', type: 'int' }] });
  } else if (type === 'financial') {
    T.push({ title: 'By Status', rows: (data.summary?.statusBreakdown || []).map((s) => ({
      status: statusLabel(s.status), count: num(s.count), amount: num(s.amount), pctv: num(s.percentage) })),
      columns: [{ key: 'status', label: 'Status', type: 'text' }, { key: 'count', label: 'Claims', type: 'int' },
        { key: 'amount', label: 'Amount', type: 'money' }, { key: 'pctv', label: '% of $', type: 'pct' }] });
    T.push({ title: 'Revenue by Customer', rows: (data.revenueByCustomer || []).map((c) => ({
      name: c.customer?.name || '—', count: num(c.count), amount: num(c.totalAmount) })),
      columns: [{ key: 'name', label: 'Customer', type: 'text' }, { key: 'count', label: 'Chargebacks', type: 'int' }, { key: 'amount', label: 'Amount', type: 'money' }] });
    T.push({ title: 'Monthly', rows: (data.monthlyRevenue || []).map((m) => ({
      period: m.period, count: num(m.count), amount: num(m.totalAmount) })),
      columns: [{ key: 'period', label: 'Period', type: 'text' }, { key: 'count', label: 'Claims', type: 'int' }, { key: 'amount', label: 'Amount', type: 'money' }] });
  } else if (type === 'edi') {
    T.push({ title: 'Batches', rows: (data.batches || []).map((b) => ({
      batch: b.batch?.batchNumber || '—', status: statusLabel(b.batch?.status), records: num(b.batch?.recordCount), amount: num(b.totalAmount) })),
      columns: [{ key: 'batch', label: 'Batch', type: 'text' }, { key: 'status', label: 'Status', type: 'text' },
        { key: 'records', label: 'Records', type: 'int' }, { key: 'amount', label: 'Amount', type: 'money' }] });
    T.push({ title: 'By Status', rows: (data.summary || []).map((s) => ({
      status: statusLabel(s.status), batches: num(s._count?.status), records: num(s._sum?.recordCount) })),
      columns: [{ key: 'status', label: 'Status', type: 'text' }, { key: 'batches', label: 'Batches', type: 'int' }, { key: 'records', label: 'Records', type: 'int' }] });
  }
  return T;
}

export default function ReportDataView({ type, data }) {
  if (data && typeof data === 'object' && !Array.isArray(data) && 'success' in data && data.data) data = data.data;
  if (!data) return <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-10 text-center text-sm text-gray-500">No data available.</div>;
  const tables = tablesFor(type, data);
  if (!tables.length) return <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-10 text-center text-sm text-gray-500">No tabular view for this report.</div>;
  return (
    <div className="space-y-5">
      {tables.map((t, i) => <DataTable key={i} {...t} />)}
    </div>
  );
}
